<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { storeToRefs } from 'pinia';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SectionLayout from '../../account/components/SectionLayout.vue';
import FeatureToggle from '../components/FeatureToggle.vue';
import ModelSelector from '../components/ModelSelector.vue';
import PipelineStageSelector from '../components/PipelineStageSelector.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  insightsConfig: { type: Object, required: true },
});

const emit = defineEmits(['featureToggle', 'modelChange', 'configChange']);

const { t } = useI18n();
const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatwootCloud } = useAccount();

const captainConfigStore = useCaptainConfigStore();
const { features } = storeToRefs(captainConfigStore);

const isPassiveEnabled = computed(
  () => features.value?.insights_passive?.enabled ?? false
);
const isActiveEnabled = computed(
  () => features.value?.insights_active?.enabled ?? false
);

const isFeatureAccessible = () => {
  if (isOnChatwootCloud.value) return true;
  return isEnterprise && enterprisePlanName === 'enterprise';
};

const handleFeatureToggle = ({ feature, enabled }) => {
  emit('featureToggle', { feature, enabled });
};

const handleModelChange = ({ feature, model }) => {
  emit('modelChange', { feature, model });
};

const updateConfigField = (field, value) => {
  emit('configChange', {
    section: 'insights',
    data: { [field]: value },
  });
};

// --- Deal insights config ---
const FREQUENCY_OPTIONS = [
  {
    value: 'realtime',
    labelKey: 'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.FREQUENCY.REALTIME',
  },
  {
    value: 'daily',
    labelKey: 'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.FREQUENCY.DAILY',
  },
  {
    value: 'weekly',
    labelKey: 'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.FREQUENCY.WEEKLY',
  },
];

const currentFrequency = computed(
  () => props.insightsConfig.deal_frequency || 'realtime'
);
const currentMonitoredPipelines = computed(
  () => props.insightsConfig.monitored_pipelines || {}
);

const handleFrequencyChange = event => {
  updateConfigField('deal_frequency', event.target.value);
};

const handlePipelinesChange = value => {
  updateConfigField('monitored_pipelines', value);
};

// --- Conversation insights config ---
const TRIGGER_OPTIONS = [
  {
    value: 'on_close',
    labelKey: 'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.TRIGGER.ON_CLOSE',
  },
  {
    value: 'on_demand',
    labelKey:
      'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.TRIGGER.ON_DEMAND',
  },
];

const OUTPUT_OPTIONS = [
  {
    value: 'summary',
    labelKey: 'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.OUTPUTS.SUMMARY',
  },
  {
    value: 'sentiment',
    labelKey:
      'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.OUTPUTS.SENTIMENT',
  },
  {
    value: 'action_items',
    labelKey:
      'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.OUTPUTS.ACTION_ITEMS',
  },
];

const currentTrigger = computed(
  () => props.insightsConfig.conversation_trigger || 'on_close'
);
const currentOutputs = computed(
  () =>
    props.insightsConfig.conversation_outputs || [
      'summary',
      'sentiment',
      'action_items',
    ]
);

const handleTriggerChange = event => {
  updateConfigField('conversation_trigger', event.target.value);
};

const isOutputSelected = value => {
  return currentOutputs.value.includes(value);
};

const toggleOutput = value => {
  const current = [...currentOutputs.value];
  const idx = current.indexOf(value);
  if (idx >= 0) {
    if (current.length <= 1) return;
    current.splice(idx, 1);
  } else {
    current.push(value);
  }
  updateConfigField('conversation_outputs', current);
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Description -->
    <div class="pb-2">
      <p class="text-sm text-n-slate-11 mb-0">
        {{ t('CAPTAIN_SETTINGS.TABS.INSIGHTS.INTRO') }}
      </p>
    </div>

    <!-- Passive Insights (Daily Briefing) -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.TABS.INSIGHTS.PASSIVE.TITLE')"
      :description="t('CAPTAIN_SETTINGS.TABS.INSIGHTS.PASSIVE.DESCRIPTION')"
    >
      <div class="grid gap-4">
        <FeatureToggle
          feature-key="insights_passive"
          :is-allowed="isFeatureAccessible()"
          @change="handleFeatureToggle"
          @model-change="handleModelChange"
        />
      </div>
    </SectionLayout>

    <!-- Active Insights (Real-time Monitoring) -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.TABS.INSIGHTS.ACTIVE.TITLE')"
      :description="t('CAPTAIN_SETTINGS.TABS.INSIGHTS.ACTIVE.DESCRIPTION')"
      with-border
    >
      <div class="grid gap-4">
        <FeatureToggle
          feature-key="insights_active"
          :is-allowed="isFeatureAccessible()"
          @change="handleFeatureToggle"
          @model-change="handleModelChange"
        />
      </div>
    </SectionLayout>

    <!-- Model Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
      :description="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.MODEL_DESC')"
      with-border
    >
      <div class="grid gap-4">
        <ModelSelector
          :is-allowed="isFeatureAccessible()"
          feature-key="insights_passive"
          :title="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.MODEL_PASSIVE')"
          :description="
            t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.MODEL_PASSIVE_DESC')
          "
          @change="handleModelChange"
        />
        <ModelSelector
          :is-allowed="isFeatureAccessible()"
          feature-key="insights_active"
          :title="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.MODEL_ACTIVE')"
          :description="
            t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.MODEL_ACTIVE_DESC')
          "
          @change="handleModelChange"
        />
      </div>
    </SectionLayout>

    <!-- Deal Insights Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.TITLE')"
      :description="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.DESCRIPTION')"
      with-border
    >
      <div
        class="flex flex-col gap-4"
        :class="{ 'opacity-50 pointer-events-none': !isActiveEnabled }"
      >
        <!-- Frequency -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.DEAL.FREQUENCY.LABEL') }}
          </label>
          <select
            :value="currentFrequency"
            class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
            @change="handleFrequencyChange"
          >
            <option
              v-for="option in FREQUENCY_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.labelKey) }}
            </option>
          </select>
        </div>

        <!-- Pipeline/Stage Selector -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.LABEL') }}
          </label>
          <p class="text-xs text-n-slate-11 mb-1">
            {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.HELP') }}
          </p>
          <PipelineStageSelector
            :model-value="currentMonitoredPipelines"
            :disabled="!isActiveEnabled"
            @update:model-value="handlePipelinesChange"
          />
        </div>
      </div>
    </SectionLayout>

    <!-- Conversation Insights Configuration -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.TITLE')"
      :description="
        t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.DESCRIPTION')
      "
      with-border
    >
      <div
        class="flex flex-col gap-4"
        :class="{ 'opacity-50 pointer-events-none': !isPassiveEnabled }"
      >
        <!-- Trigger -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.TRIGGER.LABEL'
              )
            }}
          </label>
          <select
            :value="currentTrigger"
            class="h-10 px-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9"
            @change="handleTriggerChange"
          >
            <option
              v-for="option in TRIGGER_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.labelKey) }}
            </option>
          </select>
        </div>

        <!-- Outputs -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{
              t(
                'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.OUTPUTS.LABEL'
              )
            }}
          </label>
          <p class="text-xs text-n-slate-11 mb-1">
            {{
              t(
                'CAPTAIN_SETTINGS.INSIGHTS_CONFIG.CONVERSATION.OUTPUTS.HELP'
              )
            }}
          </p>
          <div class="flex flex-col gap-2">
            <div
              v-for="option in OUTPUT_OPTIONS"
              :key="option.value"
              class="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-n-alpha-black2 cursor-pointer transition-colors"
              @click="toggleOutput(option.value)"
            >
              <Checkbox
                :model-value="isOutputSelected(option.value)"
                @click.stop
                @change="toggleOutput(option.value)"
              />
              <span class="text-sm text-n-slate-12">
                {{ t(option.labelKey) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
